export const LEVEL_GAMEPLAY_Z_INDEX = {
  BACKGROUND: 0,
  DECORS: 1,
  SPARKS: 2,
  BOOSTERS: 3,
  OBSTACLES: 4,
  PLAYER: 5,
  FX_FRONT: 6,
  OBSTACLES_FRONT: 7,
  FOREGROUND: 8,
  DEBUG_HITBOX: 9,
} as const;

/** Temporary: skip FG tiles (render + preload) to ease phone RAM. */
export const LEVEL_FOREGROUND_ENABLED = false;

export const PLAYER_TILT_DEGREES = 25;

export const FLIGHT_THRUST_KEY = 'Space' as const;

/** Высота области геймплея под HUD (px, design 1080×1920). */
export const LEVEL_PLAYFIELD_HEIGHT_PX = 1920;

/**
 * Zoom камеры раннера на ПК:
 * `1` = без letterbox (фон заполняет playfield).
 * Значение < 1 отдаляет камеру, но оставляет пустые полосы сверху/снизу.
 */
export const PC_RUNNER_DISPLAY_ZOOM = 0.75 as const;

/**
 * Доля ширины playfield — позиция курьера на ПК (левее мобильных 0.15),
 * чтобы впереди было видно больше маршрута.
 */
export const PC_COURIER_SCREEN_X_RATIO = 0.1 as const;

/** Длительность плавного торможения и сброса наклона на финише (мс). */
export const FINISH_DECEL_MS = 5000;

/** Шаги обратного отсчёта перед стартом уровня. */
export const LEVEL_COUNTDOWN_STEPS = ['3', '2', '1', 'Старт'] as const;

/** Длительность одного шага отсчёта (мс). */
export const LEVEL_COUNTDOWN_STEP_MS = 1000;

export type RunnerTutorialHint =
  | 'controls'
  | 'obstacle'
  | 'booster'
  | 'collectible'
  | 'secondChance';

/** Тексты призрачных подсказок первой игры. */
export const RUNNER_TUTORIAL_HINT_TEXT: Record<RunnerTutorialHint, string> = {
  controls: 'нажми и полети вверх, отпусти и полети вниз',
  obstacle: 'избегай препятствия чтобы не потерять все жизни',
  booster: 'собирай бустеры чтобы получить улучшения',
  collectible: 'собирай зефирки чтобы заработать больше очков',
  secondChance:
    'У тебя закончились жизни. Пройди мини-игру, чтобы получить шанс возродиться и продолжить уровень!',
};

/** Длительность выезда/уезда лупы Willy blur (мс). */
export const WILLY_BLUR_LOUPE_MS = 600;

/** Длительность появления/скрытия blur (мс). */
export const WILLY_BLUR_FADE_MS = 500;

/** Время полной видимости blur на экране (мс). */
export const WILLY_BLUR_HOLD_MS = 10_000;

/** Длительность выезда/уезда щупалец Mona (мс). Скорость −30% → 600 / 0.7. */
export const MONA_TENTACLE_SLIDE_MS = Math.round(600 / 0.7);

/** Время полной видимости щупалец на экране (мс). */
export const MONA_TENTACLE_HOLD_MS = 10_000;

/** Окно спавна мячей duck-оверлея (мс). */
export const DUCK_BALL_SPAWN_WINDOW_MS = 10_000;

/** Длительность подлёта / ухода мяча (мс). */
export const DUCK_BALL_FLY_MS = 600;

/** Короткая пауза мяча в целевой точке (мс). */
export const DUCK_BALL_HOLD_MS = 400;

/** Минимальный интервал между спавнами мячей (мс). */
export const DUCK_BALL_SPAWN_STAGGER_MS = 80;

/** Окно мигания облаков Roza-оверлея (мс). */
export const ROZA_CLOUD_HOLD_MS = 10_000;

/** Длительность общего fade-out облаков в конце (мс). */
export const ROZA_CLOUD_OUT_MS = 500;

/**
 * Полный цикл мигания одного облака (мс):
 * fadeIn 350 + visible 2200 + fadeOut 350 + hidden 1400 = 4300.
 */
export const ROZA_CLOUD_CYCLE_MS = 4300;

/** Сдвиг старта цикла между облаками (мс). */
export const ROZA_CLOUD_STAGGER_MS = 400;

/** Окно мигания облаков Serj-оверлея (мс). */
export const SERJ_CLOUD_HOLD_MS = 10_000;

/** Длительность общего fade-out облаков Serj в конце (мс). */
export const SERJ_CLOUD_OUT_MS = 500;

/**
 * Полный цикл мигания одного облака Serj (мс):
 * fadeIn 350 + visible 2200 + fadeOut 350 + hidden 1400 = 4300.
 */
export const SERJ_CLOUD_CYCLE_MS = 4300;

/** Сдвиг старта цикла между облаками Serj (мс). */
export const SERJ_CLOUD_STAGGER_MS = 400;

/** Fade in/out для svami modifier overlay (мс). */
export const SVAMI_OVERLAY_FADE_MS = 500;

/** Время полной видимости svami overlay (мс). */
export const SVAMI_OVERLAY_HOLD_MS = 10_000;

/** Fade in/out для sol modifier overlay (мс). */
export const SOL_OVERLAY_FADE_MS = 500;

/** Время полной видимости sol overlay (мс). */
export const SOL_OVERLAY_HOLD_MS = 10_000;

/** Fade in/out для factor mirage overlay (мс). */
export const FACTOR_OVERLAY_FADE_MS = 500;

/** Время полной видимости factor overlay (мс). */
export const FACTOR_OVERLAY_HOLD_MS = 10_000;

/** Окно полёта метеоров mu overlay (мс). */
export const MU_OVERLAY_HOLD_MS = 10_000;

/** Длительность одного полёта метеора (мс). Было 2500 — скорость −50%. */
export const MU_OVERLAY_FLIGHT_MS = 5000;

/** Fade in/out для yetti overlay (мс). */
export const YETTI_OVERLAY_FADE_MS = 500;

/** Длительность подъезда ice slabs (мс). */
export const YETTI_OVERLAY_ICE_SLIDE_MS = 2000;

/** Время полной видимости yetti overlay после подъезда ice (мс). */
export const YETTI_OVERLAY_HOLD_MS = 10_000;

/** Fade in/out для drakochi overlay (мс). */
export const DRAKOCHI_OVERLAY_FADE_MS = 500;

/** Время полной видимости drakochi overlay (мс). */
export const DRAKOCHI_OVERLAY_HOLD_MS = 10_000;
